import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';

const LABELS = {
  discovery: {
    site_internet: 'Site internet', reseau_sts: 'Réseau STS', youtube: 'YouTube',
    facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn',
    deja_venu: 'Déjà venu·e', recommandation: 'Recommandation', presse: 'Presse', autre: 'Autre',
  },
  situation: {
    gerant: 'Gérant·e société', auto_entrepreneur: 'Auto-entrepreneur', creation: 'En cours de création',
    salarie: 'Salarié·e', interimaire: 'Intérimaire', demandeur_emploi: "Demandeur d'emploi", retraite: 'Retraité·e',
  },
  education: {
    bac3_4: 'Bac+3/Bac+4', bac2: 'Bac+2', bac_btn_bt_bp: 'BAC · BTN · BT · BP',
    bepc_bep_cap: 'BEPC · BEP · CAP', cep_ses: 'CEP · SES',
  },
  formation: {
    decouverte: 'Découverte', generale: 'Générale', nautique: 'Nautique',
    auto: 'Auto', moto: 'Moto', habitat: 'Habitat & médical', dessin: 'Dessin technique',
  },
  handedness: { droitier: 'Droitier·e', gaucher: 'Gaucher·e' },
  sewing: { debutant: 'Débutant·e', confirme: 'Confirmé·e' },
  motivation_tags: {
    renforcer: 'Renforcer mes connaissances',
    nouvelles: 'Acquérir de nouvelles compétences',
    creation: "Projet de création d'entreprise",
    entreprise: 'Formation par mon entreprise',
  },
  particularities: { plus_30km: "+ de 30 km d'Istres", enfants_moins_10: 'Enfants < 10 ans', ras: 'RAS' },
  funding: {
    autofinancement: 'Autofinancement', ptp: 'PTP (salarié)', agefiph: 'AGEFIPH',
    region: 'Région', opco: 'OPCO', faf: 'FAF', france_travail: 'France Travail', autre: 'Autre',
  },
};

const labelFor = (g, k) => (k ? LABELS[g]?.[k] || k : '—');
const labelList = (g, list) => (list?.length ? list.map((k) => labelFor(g, k)).join(' · ') : '—');
const v = (x) => (x === null || x === undefined || x === '' ? '—' : x);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const Row = ({ label, value }) => (
  <tr>
    <td className="label">{label}</td>
    <td className="value">{v(value)}</td>
  </tr>
);

/**
 * Print-friendly student fiche page.
 * URL: /:lang/admin/fiche-eleve/:userId/print
 * On mount, fetches the recap and triggers window.print() so the admin can
 * save the formatted fiche as PDF directly from the browser.
 */
export default function FicheElevePrintPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api.getUserRecap(userId)
      .then((d) => {
        if (!active) return;
        setData(d);
        // Give the DOM a tick to render before opening print dialog.
        setTimeout(() => window.print(), 500);
      })
      .catch((e) => active && setError(e.response?.data?.detail || 'Erreur de chargement'));
    return () => { active = false; };
  }, [userId]);

  if (error) return <div style={{ padding: 40, color: '#b00020' }}>{error}</div>;
  if (!data) return <div style={{ padding: 40 }}>Chargement de la fiche…</div>;

  const u = data.user || {};
  const personal = data.personal || {};
  const latest = data.applications?.[0];
  const fullAddress = [personal.address_street, personal.address_complement,
    [personal.address_postcode, personal.address_city].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');

  return (
    <div className="fiche-print">
      <style>{`
        @page { size: A4; margin: 14mm 16mm; }
        body { background: #fff !important; }
        .fiche-print {
          font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
          color: #1F2A4A;
          max-width: 800px;
          margin: 0 auto;
          padding: 24px;
          background: #fff;
        }
        .fiche-print h1 {
          font-family: 'Cormorant Garamond', 'Georgia', serif;
          font-weight: 300;
          font-size: 32px;
          margin: 0 0 4px 0;
        }
        .fiche-print h1 em { color: #C9B896; font-style: italic; }
        .fiche-print h2 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 400;
          font-size: 18px;
          margin: 24px 0 8px 0;
          padding-bottom: 4px;
          border-bottom: 1px solid #C9B896;
          color: #1F2A4A;
        }
        .fiche-print .kicker {
          font-size: 10px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: #C9B896;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .fiche-print .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 20px;
          padding-bottom: 14px;
          border-bottom: 2px solid #1F2A4A;
        }
        .fiche-print .header .logo {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-size: 22px;
          color: #1F2A4A;
        }
        .fiche-print .header .logo small { display: block; font-size: 9px; letter-spacing: 0.3em; color: #C9B896; font-family: 'Roboto', sans-serif; font-style: normal; }
        .fiche-print .header .right { text-align: right; font-size: 11px; color: #4A5568; }
        .fiche-print table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        .fiche-print td { padding: 6px 8px; vertical-align: top; font-size: 12px; }
        .fiche-print td.label {
          width: 38%; color: #4A5568; font-size: 10px;
          text-transform: uppercase; letter-spacing: 0.08em; font-weight: 500;
        }
        .fiche-print td.value { color: #1F2A4A; font-weight: 400; }
        .fiche-print tr { border-bottom: 1px solid #f0ece2; }
        .fiche-print .badge {
          display: inline-block; padding: 2px 8px; font-size: 10px;
          background: #FAF7F0; border: 1px solid #C9B896; color: #1F2A4A;
          border-radius: 999px; margin-right: 4px;
        }
        .fiche-print .footer {
          margin-top: 30px; padding-top: 12px; border-top: 1px solid #ddd;
          font-size: 9px; color: #888; text-align: center;
        }
        @media print {
          .fiche-print { padding: 0; max-width: none; }
          .no-print { display: none; }
        }
      `}</style>

      <div className="header">
        <div className="logo">
          Sellerie Académie
          <small>FICHE ÉLÈVE</small>
        </div>
        <div className="right">
          Édité le {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <p className="kicker">IDENTITÉ</p>
      <h1>{u.first_name} <em>{u.last_name}</em></h1>
      <p style={{ fontSize: 12, color: '#4A5568', margin: 0 }}>
        Inscrit·e le {formatDate(u.created_at)} · Rôle : {u.role}
      </p>

      <h2>Coordonnées</h2>
      <table>
        <tbody>
          <Row label="Email" value={u.email} />
          <Row label="Téléphone" value={u.phone} />
          <Row label="Âge" value={personal.age} />
          <Row label="Adresse" value={fullAddress} />
          <Row label="Particularités" value={labelList('particularities', personal.particularities)} />
        </tbody>
      </table>

      {latest && (
        <>
          <h2>Découverte & situation</h2>
          <table>
            <tbody>
              <Row label="Canaux de découverte" value={labelList('discovery', latest.discovery_channels)} />
              <Row label="Nom du parrain" value={personal.referrer_name} />
              <Row label="Situation actuelle" value={labelFor('situation', latest.current_situation)} />
              <Row label="Niveau d'études" value={labelFor('education', personal.education_level)} />
              <Row label="Parcours professionnel" value={personal.professional_history} />
              <Row label="Poste actuel" value={personal.current_job_description} />
              <Row label="ID France Travail" value={personal.france_travail_id} />
            </tbody>
          </table>

          <h2>Formation visée</h2>
          <table>
            <tbody>
              <Row label="Formation" value={labelFor('formation', personal.target_formation)} />
              <Row label="Période souhaitée" value={personal.preferred_period} />
              <Row label="Latéralité" value={labelFor('handedness', personal.handedness)} />
              <Row label="Niveau machine à coudre" value={labelFor('sewing', personal.sewing_machine_level)} />
              <Row label="Motivation (tags)" value={labelList('motivation_tags', personal.motivation_tags)} />
              <Row label="Attentes" value={personal.expectations} />
              <Row label="Motivation détaillée" value={latest.motivation} />
              <Row label="Expérience" value={latest.experience} />
            </tbody>
          </table>

          <h2>Financement & accessibilité</h2>
          <table>
            <tbody>
              <Row label="RQTH" value={personal.has_rqth} />
              <Row label="Source de financement principale" value={labelFor('funding', latest.funding_source)} />
              <Row label="Options envisagées" value={labelList('funding', latest.funding_options)} />
            </tbody>
          </table>
        </>
      )}

      <h2>Historique candidatures ({data.applications?.length || 0})</h2>
      {data.applications?.length ? (
        <table>
          <tbody>
            {data.applications.map((a) => (
              <Row key={a.id} label={formatDate(a.created_at)} value={`${a.course_title || a.course_id} — ${a.status}`} />
            ))}
          </tbody>
        </table>
      ) : <p style={{ fontSize: 12, color: '#888' }}>Aucune candidature.</p>}

      <h2>Inscriptions actives ({data.enrollments?.length || 0})</h2>
      {data.enrollments?.length ? (
        <table>
          <tbody>
            {data.enrollments.map((e) => (
              <Row key={e.id} label={formatDate(e.created_at)} value={e.course_title || e.course_id} />
            ))}
          </tbody>
        </table>
      ) : <p style={{ fontSize: 12, color: '#888' }}>Aucune inscription.</p>}

      <h2>Paiements ({data.payments?.length || 0})</h2>
      {data.payments?.length ? (
        <table>
          <tbody>
            {data.payments.map((p) => (
              <Row key={p.id} label={formatDate(p.created_at)} value={`${p.amount} € · ${p.method || '—'} · ${p.status}`} />
            ))}
          </tbody>
        </table>
      ) : <p style={{ fontSize: 12, color: '#888' }}>Aucun paiement.</p>}

      <div className="footer">
        Sellerie Académie · Document confidentiel — usage interne uniquement.
      </div>

      <div className="no-print" style={{ marginTop: 24, textAlign: 'center' }}>
        <button
          onClick={() => window.print()}
          style={{ padding: '10px 22px', background: '#C9B896', color: '#1F2A4A', border: 'none', borderRadius: 999, fontWeight: 500, cursor: 'pointer' }}
        >
          Imprimer / Enregistrer en PDF
        </button>
      </div>
    </div>
  );
}
